import { Box, Stack } from '@mui/material';
import { t } from 'i18next';
import { useMemo } from 'react';
import { NativeSelect } from '../../inputs/NativeSelect';
import { Operation } from '../enum/Operation';
import { Expression, FilterConfiguration } from '../types';
import { getDefaultExpression } from '../utils';
import { getValueInputComponent } from './helpers/getValueInputComponent';

type Props = {
  expression: Expression;
  filters: FilterConfiguration[];
  onChange: (newExpression: Expression) => void;
  errorMessage?: string;
};

export const ExpressionField = ({ expression, filters, onChange, errorMessage }: Props) => {
  const filter = filters.find(({ filterKey }) => filterKey === expression.field) as FilterConfiguration;
  const fields = filters.map(({ label, filterKey }) => ({ label, value: filterKey }));

  const ValueInput = useMemo(() => getValueInputComponent(filter.type), [filter.type]);

  const handleChange = (changedData: Partial<Expression>) => {
    const newExpression = { ...expression, ...changedData } as Expression;
    // We explicitly know all the types of fields, so it should always be defined here.
    const newFilterConfig = filters.find(({ filterKey }) => filterKey === newExpression.field) as FilterConfiguration;

    // if field has changed, reset the value and operation
    if (newExpression.field !== expression.field) {
      const defaultValue = getDefaultExpression(newFilterConfig);
      newExpression.value = defaultValue.value;
      newExpression.operation = defaultValue.operation;
    }

    onChange(newExpression);
  };

  return (
    <Stack direction="row" alignItems="start" width={1} gap={1}>
      <Box sx={{ flex: 1.25 }}>
        <NativeSelect label={t('filterBy')} options={fields} value={expression.field} onChange={(field) => handleChange({ field })} autoFocus />
      </Box>
      <Box sx={{ flex: 1 }}>
        <NativeSelect
          label={t('operations')}
          options={filter.operations}
          value={expression.operation}
          onChange={(operation) => handleChange({ operation: operation as Operation })}
        />
      </Box>
      <Box sx={{ flex: 2 }}>
        <ValueInput
          key={expression.field}
          filter={filter}
          value={expression.value}
          operation={expression.operation}
          onChange={(value) => handleChange({ value } as Partial<Expression>)}
          errorMessage={errorMessage}
        />
      </Box>
    </Stack>
  );
};
